import { MantineTheme, parseThemeColor } from '@mantine/core';
import { ChatChartSpecDto } from 'src/api/FiretrolApiClient';
import {
  CHART_VERTICAL_MARGIN,
  CHAT_CHART_COLORS,
  ChatChartLayout,
  DEFAULT_X_AXIS_HEIGHT,
  DisplayableChatChartSpec,
  LEGEND_HEIGHT,
  chooseChartLayout,
  isDisplayableChart,
} from 'src/app/chat/chatChartFormat';

// Fits the printable width of both Letter (695px) and A4 (673px) inside the 16mm @page margins.
export const PRINT_CHART_WIDTH = 640;
// Letter's printable height is 935px; this leaves room for the chart title and a legend on the same page.
export const MAX_PRINT_CHART_HEIGHT = 800;
// A 12px tick label needs about 14px; below 16px per row, neighbouring labels touch.
export const MIN_PRINT_ROW_HEIGHT = 16;

export const PRINT_CHART_TOO_TALL_TEXT = 'Too many rows to chart; data below.';

export type PrintChartPlan =
  | { kind: 'table' }
  | { kind: 'too-tall' }
  | { kind: 'pie'; spec: DisplayableChatChartSpec }
  | {
      kind: 'cartesian';
      spec: DisplayableChatChartSpec;
      layout: ChatChartLayout;
      svgHeight: number;
    };

/** @registry Resolves the AI chat chart palette to literal colors; the print document has no Mantine CSS variables. */
export const resolvePrintChartColors = (theme: MantineTheme): string[] =>
  CHAT_CHART_COLORS.map((color) => parseThemeColor({ color, theme }).value);

/**
 * Decides how a chart prints: the on-screen layout at the print width, with
 * horizontal-bar rows shrunk to fit one page, or table-only when the spec
 * cannot be drawn or has too many rows to fit even at the minimum row height.
 */
export const planPrintChart = (spec: ChatChartSpecDto): PrintChartPlan => {
  if (!isDisplayableChart(spec)) return { kind: 'table' };
  if (spec.type === 'pie') return { kind: 'pie', spec };
  const layout = chooseChartLayout(spec, PRINT_CHART_WIDTH);
  const legendHeight = spec.series.length >= 2 ? LEGEND_HEIGHT : 0;
  const svgHeight = layout.height - legendHeight;
  if (svgHeight <= MAX_PRINT_CHART_HEIGHT) {
    return { kind: 'cartesian', spec, layout, svgHeight };
  }
  const rowSpace =
    MAX_PRINT_CHART_HEIGHT - DEFAULT_X_AXIS_HEIGHT - CHART_VERTICAL_MARGIN;
  if (
    layout.kind !== 'rows' ||
    rowSpace / spec.points.length < MIN_PRINT_ROW_HEIGHT
  ) {
    return { kind: 'too-tall' };
  }
  return {
    kind: 'cartesian',
    spec,
    layout,
    svgHeight: MAX_PRINT_CHART_HEIGHT,
  };
};
